// Adds mousedown/mousemove/mouseup and touchstart/touchmove/touchend events to
// HTML element 'target', transforming the events into into sequences of:
//  - startSingle -> [multiple] moveSingle -> endSingle
//  - startDouble -> [multiple] moveDouble -> endDouble
// NOTE: endSingle -> startDouble happens when second finger hits;
//       endDouble -> startSingle happens when second finger lifted, etc.

const addFancyTouchListener = (
  target,
  handlers = {
    startSingle: (p) => {},
    moveSingle: (p) => {},
    endSingle: () => {},
    startDouble: (p, q) => {},
    moveDouble: (p, q) => {},
    endDouble: () => {},
  },
) => {
  // Wrappers to prevent multiple endSingle/endDouble calls
  // ---------------------------------------------------------------------------
  let startedSingleWithoutEnd = false;
  let startedDoubleWithoutEnd = false;

  const _startSingle = (touchP) => {
    handlers.startSingle(touchP);
    startedSingleWithoutEnd = true;
  };

  const _endSingle = () => {
    if (startedSingleWithoutEnd) {
      startedSingleWithoutEnd = false;
      handlers.endSingle();
    }
  };

  const _startDouble = (touchP, touchQ) => {
    handlers.startDouble(touchP, touchQ);
    startedDoubleWithoutEnd = true;
  };

  const _endDouble = () => {
    if (startedDoubleWithoutEnd) {
      startedDoubleWithoutEnd = false;
      handlers.endDouble();
    }
  };

  // Transform events with points and touch counts into sequences of callbacks
  // ---------------------------------------------------------------------------
  let startSingleOnNextMove = false;

  const doStart = (touchCount, touchP, touchQ) => {
    // transition from 0 to 1
    if (touchCount === 1) {
      _startSingle(touchP);
    }
    // transition from 1 to 2
    else if (touchCount === 2) {
      _endSingle();
      _startDouble(touchP, touchQ);
    }
  };

  const doMove = (touchCount, touchP, touchQ) => {
    // single touch move
    if (touchCount === 1) {
      if (startSingleOnNextMove) {
        startSingleOnNextMove = false;
        _startSingle(touchP);
      }
      handlers.moveSingle(touchP);
    }
    // double touch move
    else if (touchCount === 2) {
      handlers.moveDouble(touchP, touchQ);
    }
  };

  const doEnd = (touchesRemaining) => {
    // transition from 2 to 1
    if (touchesRemaining === 1) {
      _endDouble();
      startSingleOnNextMove = true;
    }
    // transition from 1 to 0
    else if (touchesRemaining === 0) {
      _endSingle();
    }
  };

  // Event listeners: Pass points and touch counts to doStart, doMove, doEnd
  // ---------------------------------------------------------------------------
  const offsetPoint = (t) => {
    const { left, top } = target.getBoundingClientRect();
    return [t.clientX - left, t.clientY - top];
  };

  const preventDefault = (f) => (e) => {
    f(e);
    e.preventDefault();
  };

  const onMouseDown = preventDefault((e) => {
    // left button mousedown
    if (e.buttons === 1) {
      const touchP = offsetPoint(e);
      doStart(1, touchP, null);
    }
  });

  const onTouchStart = preventDefault((e) => {
    // single touch start
    if (e.touches.length === 1) {
      const touchP = offsetPoint(e.touches[0]);
      doStart(1, touchP, null);
    }
    // double touch start
    else if (e.touches.length >= 2) {
      const touchP = offsetPoint(e.touches[0]);
      const touchQ = offsetPoint(e.touches[1]);
      doStart(2, touchP, touchQ);
    }
  });

  const onMouseMove = preventDefault((e) => {
    // left button mousedrag
    if (e.buttons === 1) {
      const touchP = offsetPoint(e);
      doMove(1, touchP, null);
    }
  });

  const onTouchMove = preventDefault((e) => {
    // single touch move
    if (e.touches.length === 1) {
      const touchP = offsetPoint(e.touches[0]);
      doMove(1, touchP, null);
    }
    // double touch move
    else if (e.touches.length >= 2) {
      const touchP = offsetPoint(e.touches[0]);
      const touchQ = offsetPoint(e.touches[1]);
      doMove(2, touchP, touchQ);
    }
  });

  const onMouseUp = preventDefault(() => {
    const touchesRemaining = 0;
    doEnd(touchesRemaining);
  });

  const onTouchEnd = preventDefault((e) => {
    const touchesRemaining = e.touches.length;
    doEnd(touchesRemaining);
  });

  target.addEventListener('mousedown', onMouseDown, false);
  target.addEventListener('touchstart', onTouchStart, false);
  target.addEventListener('mousemove', onMouseMove, false);
  target.addEventListener('touchmove', onTouchMove, false);
  target.addEventListener('mouseup', onMouseUp, false);
  target.addEventListener('touchend', onTouchEnd, false);
  return () => {
    target.removeEventListener('mousedown', onMouseDown, false);
    target.removeEventListener('touchstart', onTouchStart, false);
    target.removeEventListener('mousemove', onMouseMove, false);
    target.removeEventListener('touchmove', onTouchMove, false);
    target.removeEventListener('mouseup', onMouseUp, false);
    target.removeEventListener('touchend', onTouchEnd, false);
  };
};

export default addFancyTouchListener;
